import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../hooks/useApi';
import logger from '../../../utils/logger';
import './AdminAiAgentLogs.css';
import { fmtDateTime } from '../../../utils/ruDates';

const PAGE_SIZE = 50;
const REFRESH_MS = 5000;

const STATUS_LABELS = {
    pending: 'Думает',
    completed: 'Готово',
    failed: 'Ошибка',
    interrupted: 'Прерван',
};

const STEP_STATUS_LABELS = {
    running: 'выполняется',
    ok: 'ok',
    error: 'ошибка',
    timeout: 'таймаут',
};

const FILTERS = [
    { value: '', label: 'Все' },
    { value: 'pending', label: 'Думает' },
    { value: 'completed', label: 'Готово' },
    { value: 'failed', label: 'Ошибка' },
    { value: 'interrupted', label: 'Прерван' },
];

function formatDuration(ms) {
    if (ms === null || ms === undefined) return '—';
    if (ms < 1000) return `${ms} мс`;
    const sec = ms / 1000;
    if (sec < 60) return `${sec.toFixed(1)} с`;
    const min = Math.floor(sec / 60);
    return `${min} мин ${Math.round(sec % 60)} с`;
}

function formatTime(iso) {
    if (!iso) return '—';
    return fmtDateTime(iso);
}

function stepTitle(step) {
    if (step.kind === 'llm') return 'Модель';
    if (step.kind === 'stop') return 'Остановка';
    return step.tool_name || 'Инструмент';
}

function StatusBadge({ status }) {
    return <span className={`aal-badge aal-badge--${status}`}>{STATUS_LABELS[status] || status}</span>;
}

function StepRow({ step }) {
    const [open, setOpen] = useState(step.status !== 'ok');
    const hasArgs = step.args && Object.keys(step.args).length > 0;
    return (
        <li className={`aal-step aal-step--${step.status}`}>
            <button type="button" className="aal-step__head" onClick={() => setOpen((v) => !v)}>
                <span className="aal-step__no">{step.step_no}</span>
                <span className={`aal-step__kind aal-step__kind--${step.kind}`}>{stepTitle(step)}</span>
                <span className="aal-step__status">{STEP_STATUS_LABELS[step.status] || step.status}</span>
                <span className="aal-step__time">{formatDuration(step.duration_ms)}</span>
                {step.result_chars ? <span className="aal-step__size">{step.result_chars.toLocaleString('ru-RU')} симв.</span> : null}
            </button>
            {open && (
                <div className="aal-step__body">
                    {hasArgs && (
                        <>
                            <div className="aal-step__label">Аргументы</div>
                            <pre className="aal-pre">{JSON.stringify(step.args, null, 2)}</pre>
                        </>
                    )}
                    {step.error && (
                        <>
                            <div className="aal-step__label">Ошибка</div>
                            <pre className="aal-pre aal-pre--error">{step.error}</pre>
                        </>
                    )}
                    {step.result_preview && (
                        <>
                            <div className="aal-step__label">Результат (начало)</div>
                            <pre className="aal-pre">{step.result_preview}</pre>
                        </>
                    )}
                    {step.status === 'running' && <div className="aal-muted">Шаг ещё выполняется — начат {formatTime(step.started_at)}</div>}
                </div>
            )}
        </li>
    );
}

function RunDetail({ run }) {
    if (!run) return <div className="aal-empty">Выберите ответ слева, чтобы увидеть шаги агента.</div>;
    return (
        <div className="aal-detail">
            <div className="aal-detail__meta">
                <StatusBadge status={run.status} />
                <span>{run.user_email}</span>
                {run.workspace_name && <span>· {run.workspace_name}</span>}
                <span>· {formatTime(run.created_at)}</span>
            </div>
            <div className="aal-step__label">Вопрос</div>
            <div className="aal-text">{run.question || '—'}</div>

            <div className="aal-step__label">Шаги ({run.steps.length})</div>
            {run.steps.length === 0 ? (
                <div className="aal-muted">Шагов нет: ответ создан до включения журнала или агент не запускался (лимит сообщений).</div>
            ) : (
                <ol className="aal-steps">
                    {run.steps.map((step) => <StepRow key={step.id} step={step} />)}
                </ol>
            )}

            <div className="aal-step__label">Ответ</div>
            {run.status === 'pending' ? (
                <div className="aal-muted">Агент ещё работает…</div>
            ) : (
                <div className={`aal-text ${run.status !== 'completed' ? 'aal-text--error' : ''}`}>{run.answer || '—'}</div>
            )}
            {run.feedback_vote && (
                <div className="aal-muted">
                    Оценка пользователя: {run.feedback_vote === 'like' ? '👍' : '👎'} {run.feedback_comment || ''}
                </div>
            )}
        </div>
    );
}

function AdminAiAgentLogs() {
    const { request } = useApi();
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(0);
    const [runs, setRuns] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [selected, setSelected] = useState(null);

    const loadRuns = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams({ limit: PAGE_SIZE, offset: page * PAGE_SIZE });
            if (status) params.set('status', status);
            const data = await request(`/api/v1/admin/ai-chat/runs?${params.toString()}`);
            setRuns(data?.items || []);
            setTotal(data?.total || 0);
        } catch (error) {
            logger.error('Failed to load AI agent runs', error);
        } finally {
            setLoading(false);
        }
    }, [request, status, page]);

    const loadRun = useCallback(async (id) => {
        if (!id) return;
        try {
            setSelected(await request(`/api/v1/admin/ai-chat/runs/${id}`));
        } catch (error) {
            logger.error('Failed to load AI agent run', error);
        }
    }, [request]);

    useEffect(() => { loadRuns(); }, [loadRuns]);
    useEffect(() => { setSelected(null); loadRun(selectedId); }, [selectedId, loadRun]);

    // Пока где-то агент думает — обновляем, чтобы видеть, на каком шаге он сейчас.
    const hasPending = runs.some((r) => r.status === 'pending') || selected?.status === 'pending';
    useEffect(() => {
        if (!hasPending) return undefined;
        const timer = setInterval(() => {
            loadRuns();
            if (selectedId) loadRun(selectedId);
        }, REFRESH_MS);
        return () => clearInterval(timer);
    }, [hasPending, loadRuns, loadRun, selectedId]);

    const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    return (
        <div className="admin-page aal">
            <div className="aal-header">
                <div>
                    <h1>Логи AI-агента</h1>
                    <p className="aal-muted">Каждый ответ агента по шагам: вызовы модели и инструментов, время, аргументы и результаты.</p>
                </div>
                <button type="button" className="aal-button" onClick={() => { loadRuns(); loadRun(selectedId); }} disabled={loading}>
                    {loading ? 'Загрузка…' : 'Обновить'}
                </button>
            </div>

            <div className="aal-filters">
                {FILTERS.map((f) => (
                    <button
                        key={f.value || 'all'}
                        type="button"
                        className={`aal-chip ${status === f.value ? 'aal-chip--active' : ''}`}
                        onClick={() => { setStatus(f.value); setPage(0); }}
                    >
                        {f.label}
                    </button>
                ))}
                <span className="aal-muted">Всего: {total}</span>
            </div>

            <div className="aal-layout">
                <div className="aal-list">
                    {runs.length === 0 && !loading && <div className="aal-empty">Ответов нет.</div>}
                    {runs.map((run) => (
                        <button
                            key={run.id}
                            type="button"
                            className={`aal-run ${selectedId === run.id ? 'aal-run--active' : ''}`}
                            onClick={() => setSelectedId(run.id)}
                        >
                            <div className="aal-run__top">
                                <StatusBadge status={run.status} />
                                <span className="aal-muted">{formatTime(run.created_at)}</span>
                                <span className="aal-run__duration">{formatDuration(run.duration_ms)}</span>
                            </div>
                            <div className="aal-run__question">{run.question || '—'}</div>
                            <div className="aal-run__bottom">
                                <span>{run.user_email}</span>
                                <span>шагов {run.steps_count}, инструментов {run.tools_count}</span>
                                {run.errors_count > 0 && <span className="aal-run__errors">ошибок {run.errors_count}</span>}
                            </div>
                            {run.status === 'pending' && run.last_step && (
                                <div className="aal-run__now">
                                    Сейчас: {stepTitle(run.last_step)} — {STEP_STATUS_LABELS[run.last_step.status] || run.last_step.status}
                                </div>
                            )}
                        </button>
                    ))}
                    {pages > 1 && (
                        <div className="aal-pager">
                            <button type="button" className="aal-button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>←</button>
                            <span>{page + 1} / {pages}</span>
                            <button type="button" className="aal-button" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)}>→</button>
                        </div>
                    )}
                </div>
                <RunDetail run={selected} />
            </div>
        </div>
    );
}

export default AdminAiAgentLogs;
